'use client';

import { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, X, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { useNotificationsSafe } from '@/lib/context/NotificationContext';
import Link from 'next/link';

interface Notification {
  id: string;
  title_uk: string;
  title_en: string;
  message_uk: string;
  message_en: string;
  type: string;
  content_id: string | null;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { language, t } = useLanguage();

  // Use safe hook that returns null if not in provider
  const notificationContext = useNotificationsSafe();

  // Close dropdown when clicking outside - must be before conditional return
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // If no context, don't render anything
  if (!notificationContext) {
    return null;
  }

  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } = notificationContext;

  // Get localized content
  const getTitle = (notification: Notification) =>
    language === 'uk' ? notification.title_uk : notification.title_en;

  const getMessage = (notification: Notification) =>
    language === 'uk' ? notification.message_uk : notification.message_en;

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo').replace('{count}', String(diffMins));
    if (diffHours < 24) return t('notifications.hoursAgo').replace('{count}', String(diffHours));
    if (diffDays < 7) return t('notifications.daysAgo').replace('{count}', String(diffDays));

    return date.toLocaleDateString(language === 'uk' ? 'uk-UA' : 'en-US', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 h-auto"
        aria-label={t('notifications.title')}
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-900">{t('notifications.title')}</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 p-0 h-auto"
                  title={t('notifications.markAllRead')}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="p-1 h-auto"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Bell className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 text-center">{t('notifications.empty')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={`relative ${!notification.is_read ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="px-4 py-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-3">
                        {/* Unread indicator */}
                        {!notification.is_read && (
                          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full" />
                        )}

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {getTitle(notification)}
                          </p>
                          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                            {getMessage(notification)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>

                        <div className="flex items-center gap-1 flex-shrink-0">
                          {notification.action_url && (
                            <Link
                              href={notification.action_url}
                              onClick={() => {
                                if (!notification.is_read) markAsRead(notification.id);
                                setIsOpen(false);
                              }}
                              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                              title={t('notifications.viewDetails')}
                            >
                              <ExternalLink className="h-4 w-4 text-gray-500" />
                            </Link>
                          )}
                          {!notification.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              className="p-1.5 h-auto"
                              title={t('notifications.markAsRead')}
                            >
                              <Check className="h-4 w-4 text-gray-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
